import type {
  CommercialBaseResponse,
  CommercialChannelRankingItem,
  CommercialChannelsQualityResponse,
  CommercialCpfQualityItem,
  CommercialFetchParams,
  CommercialFilterOption,
  CommercialFilterOptionsResponse,
  CommercialFunnelEventItem,
  CommercialFunnelEventsResponse,
  CommercialFunnelEventKey,
  CommercialFunnelBranchItem,
  CommercialFunnelResponse,
  CommercialFunnelStageItem,
  CommercialKpiKey,
  CommercialKpis,
  CommercialKpiSnapshot,
  CommercialInterestRegionItem,
  CommercialInterestRegionsResponse,
  CommercialOperationApprovalEvolutionItem,
  CommercialOperationConversionItem,
  CommercialOperationCycleItem,
  CommercialOperationMetricsResponse,
  CommercialOperationMonthlyApprovalItem,
  CommercialOperationStageTimeItem,
  CommercialOperationStuckLeadItem,
  CommercialOperationTrendSeries,
  CommercialOverviewResponse,
  CommercialRoute,
  CommercialSankeyLinkItem,
  CommercialSdrConversionItem,
  CommercialSdrConversionsResponse,
  CommercialSdrGoalItem,
  CommercialSdrGoalsResponse,
  CommercialSdrOpenWorkloadGroupKey,
  CommercialSdrOpenWorkloadItem,
  CommercialSdrOpenWorkloadResponse,
  CommercialSdrOutcomeTimeItem,
  CommercialSdrOutcomeTimeResponse,
  CommercialSdrRankingIndicator,
  CommercialSdrRankingItem,
  CommercialSdrRankingResponse,
  CommercialSdrStageTimeItem,
  CommercialSdrStageTimeResponse,
  CommercialTeamGoalProgressResponse,
  CommercialTrendGranularity,
  GoalType,
} from '../../shared/models';
import { HttpRepository } from './HttpRepository';
import { getHabitaCommercialResourceUrl } from './habitaApiUrls';

function appendQuery(url: string, params: CommercialFetchParams): string {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });

  const query = search.toString();
  return query ? `${url}${url.includes('?') ? '&' : '?'}${query}` : url;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function asNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function asNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeOption(value: unknown): CommercialFilterOption {
  const record = asRecord(value);
  const fallback = String(record.value ?? record.label ?? '').trim();

  return {
    group: typeof record.group === 'string' ? record.group : undefined,
    label: String(record.label ?? fallback),
    value: String(record.value ?? fallback),
  };
}

function normalizeRankingItem(value: unknown): CommercialChannelRankingItem {
  const record = asRecord(value);

  return {
    approved_leads: asNumber(record.approved_leads),
    approval_rate_pct: asNullableNumber(record.approval_rate_pct),
    channel: String(record.channel ?? 'Não informado'),
    cpf_clean_rate_pct: asNullableNumber(record.cpf_clean_rate_pct),
    detail: String(record.detail ?? 'Sem detalhamento'),
    mql_leads: asNumber(record.mql_leads),
    rank: record.rank === undefined || record.rank === null ? undefined : asNumber(record.rank),
    share_of_total_pct: asNullableNumber(record.share_of_total_pct),
    total_leads: asNumber(record.total_leads),
  };
}

function normalizeChannelQualityItem(value: unknown): CommercialChannelsQualityResponse['channels'][number] {
  const record = asRecord(value);
  return {
    ...normalizeRankingItem(value),
    cpf_known_leads: asNumber(record.cpf_known_leads),
    cpf_known_rate_pct: asNullableNumber(record.cpf_known_rate_pct),
  };
}

const commercialKpiKeys: CommercialKpiKey[] = [
  'approval_rate_pct',
  'approved_from_clean_rate_pct',
  'approved_leads',
  'avg_cycle_days',
  'conditional_from_clean_rate_pct',
  'conditional_leads',
  'cpf_clean_of_collected_rate_pct',
  'cpf_clean_rate_pct',
  'cpf_collected_leads',
  'cpf_collected_rate_pct',
  'cpf_dirty_rate_pct',
  'cpf_unknown_rate_pct',
  'generated_folders',
  'global_conversion_rate_pct',
  'mql_leads',
  'scheduled_from_approved_rate_pct',
  'scheduled_visits',
  'sql_leads',
  'total_leads',
];

function normalizeKpiSnapshot(value: unknown): CommercialKpiSnapshot {
  const record = asRecord(value);

  return {
    approval_rate_pct: asNullableNumber(record.approval_rate_pct),
    approved_from_clean_rate_pct: asNullableNumber(record.approved_from_clean_rate_pct),
    approved_leads: asNumber(record.approved_leads),
    avg_cycle_days: asNullableNumber(record.avg_cycle_days),
    conditional_from_clean_rate_pct: asNullableNumber(record.conditional_from_clean_rate_pct),
    conditional_leads: asNumber(record.conditional_leads),
    cpf_clean_of_collected_rate_pct: asNullableNumber(record.cpf_clean_of_collected_rate_pct),
    cpf_clean_rate_pct: asNullableNumber(record.cpf_clean_rate_pct),
    cpf_collected_leads: asNumber(record.cpf_collected_leads),
    cpf_collected_rate_pct: asNullableNumber(record.cpf_collected_rate_pct),
    cpf_dirty_leads: asNumber(record.cpf_dirty_leads),
    cpf_dirty_rate_pct: asNullableNumber(record.cpf_dirty_rate_pct),
    cpf_unknown_leads: asNumber(record.cpf_unknown_leads),
    cpf_unknown_rate_pct: asNullableNumber(record.cpf_unknown_rate_pct),
    final_outcome_leads: asNumber(record.final_outcome_leads),
    generated_folders: asNumber(record.generated_folders),
    global_conversion_rate_pct: asNullableNumber(record.global_conversion_rate_pct),
    mql_leads: asNumber(record.mql_leads),
    scheduled_from_approved_rate_pct: asNullableNumber(record.scheduled_from_approved_rate_pct),
    scheduled_visits: asNumber(record.scheduled_visits),
    sql_leads: asNumber(record.sql_leads),
    total_leads: asNumber(record.total_leads),
  };
}

function normalizeKpis(value: unknown): CommercialKpis {
  const record = asRecord(value);
  const variationsRecord = asRecord(record.variations);

  return {
    current: normalizeKpiSnapshot(record.current),
    previous: normalizeKpiSnapshot(record.previous),
    variations: commercialKpiKeys.reduce((acc, key) => ({
      ...acc,
      [key]: asNullableNumber(variationsRecord[key]),
    }), {} as CommercialKpis['variations']),
  };
}

function normalizeBaseResponse(record: Record<string, unknown>, resource: CommercialRoute): CommercialBaseResponse {
  return {
    ...(record as Partial<CommercialBaseResponse>),
    ok: record.ok === true,
    resource: String(record.resource ?? resource),
  };
}

function normalizeOverviewResponse(raw: unknown): CommercialOverviewResponse {
  const record = asRecord(raw);
  const rankings = asRecord(record.rankings);
  const totals = asRecord(record.totals);

  return {
    ...normalizeBaseResponse(record, 'overview'),
    channels: Array.isArray(record.channels) ? record.channels.map(normalizeRankingItem) : [],
    kpis: normalizeKpis(record.kpis),
    rankings: {
      approval: Array.isArray(rankings.approval) ? rankings.approval.map(normalizeRankingItem) : [],
      mql: Array.isArray(rankings.mql) ? rankings.mql.map(normalizeRankingItem) : [],
      volume: Array.isArray(rankings.volume) ? rankings.volume.map(normalizeRankingItem) : [],
    },
    totals: {
      approved_leads: asNumber(totals.approved_leads),
      approved_only_leads: asNumber(totals.approved_only_leads),
      conditional_leads: asNumber(totals.conditional_leads),
      cpf_collected_leads: asNumber(totals.cpf_collected_leads),
      cpf_dirty_leads: asNumber(totals.cpf_dirty_leads),
      cpf_unknown_leads: asNumber(totals.cpf_unknown_leads),
      generated_folders: asNumber(totals.generated_folders),
      mql_leads: asNumber(totals.mql_leads),
      scheduled_visits: asNumber(totals.scheduled_visits),
      sql_leads: asNumber(totals.sql_leads),
      total_leads: asNumber(totals.total_leads),
    },
  };
}

function normalizeOptionsResponse(raw: unknown): CommercialFilterOptionsResponse {
  const record = asRecord(raw);

  return {
    ...normalizeBaseResponse(record, 'filter-options'),
    channels: Array.isArray(record.channels) ? record.channels.map(normalizeOption) : [],
    contact_ddds: Array.isArray(record.contact_ddds)
      ? record.contact_ddds.map(normalizeOption)
      : [],
    cpf_statuses: Array.isArray(record.cpf_statuses) ? record.cpf_statuses.map(normalizeOption) : [],
    details: Array.isArray(record.details) ? record.details.map(normalizeOption) : [],
    interest_cities: Array.isArray(record.interest_cities)
      ? record.interest_cities.map(normalizeOption)
      : [],
    opportunity_statuses: Array.isArray(record.opportunity_statuses)
      ? record.opportunity_statuses.map(normalizeOption)
      : [],
    sdrs: Array.isArray(record.sdrs) ? record.sdrs.map(normalizeOption) : [],
    stages: Array.isArray(record.stages) ? record.stages.map(normalizeOption) : [],
  };
}

function normalizeFunnelStage(value: unknown): CommercialFunnelStageItem {
  const record = asRecord(value);

  return {
    conversion_rate_pct: asNullableNumber(record.conversion_rate_pct),
    lost_leads: asNumber(record.lost_leads),
    lost_rate_pct: asNullableNumber(record.lost_rate_pct),
    previous_volume: asNullableNumber(record.previous_volume),
    stage_index: asNumber(record.stage_index),
    stage_name: String(record.stage_name ?? 'Sem etapa'),
    volume: asNumber(record.volume),
  };
}

function normalizeFunnelBranch(value: unknown): CommercialFunnelBranchItem {
  const record = asRecord(value);

  return {
    anchor_stage_index: asNumber(record.anchor_stage_index),
    anchor_stage_name: String(record.anchor_stage_name ?? 'Sem etapa'),
    label: String(record.label ?? 'Desfecho'),
    outcome: String(record.outcome ?? 'disqualified') as CommercialFunnelBranchItem['outcome'],
    share_of_anchor_pct: asNullableNumber(record.share_of_anchor_pct),
    tone: String(record.tone ?? 'neutral') as CommercialFunnelBranchItem['tone'],
    volume: asNumber(record.volume),
  };
}

function normalizeFunnelResponse(raw: unknown): CommercialFunnelResponse {
  const record = asRecord(raw);

  return {
    ...normalizeBaseResponse(record, 'funnel'),
    branches: Array.isArray(record.branches) ? record.branches.map(normalizeFunnelBranch) : [],
    stages: Array.isArray(record.stages) ? record.stages.map(normalizeFunnelStage) : [],
    total_leads: asNumber(record.total_leads),
  };
}

function normalizeSdrGoalItem(value: unknown): CommercialSdrGoalItem {
  const record = asRecord(value);
  const status = String(record.status ?? 'no_goal') as CommercialSdrGoalItem['status'];
  const goalType = String(record.goal_type ?? '').toUpperCase() as GoalType;

  return {
    achieved: asNumber(record.achieved),
    daily_goal: asNullableNumber(record.daily_goal),
    expected_until_today: asNullableNumber(record.expected_until_today),
    goal_type: ['PASTAS_GERADAS', 'PASTAS_APROVADAS', 'VISITAS_AGENDADAS'].includes(goalType)
      ? goalType
      : 'PASTAS_APROVADAS',
    monthly_goal: asNumber(record.monthly_goal),
    progress_pct: asNullableNumber(record.progress_pct),
    sdr: String(record.sdr ?? 'Sem SDR'),
    status: ['attention', 'done', 'no_goal', 'on_track'].includes(status) ? status : 'no_goal',
    weekly_goal: asNullableNumber(record.weekly_goal),
  };
}

function normalizeSdrGoalsResponse(raw: unknown): CommercialSdrGoalsResponse {
  const record = asRecord(raw);
  const availableGoalTypes = Array.from(new Set(
    Array.isArray(record.available_goal_types)
      ? record.available_goal_types
        .map((value) => String(value).toUpperCase() as GoalType)
        .filter((value): value is GoalType => (
          ['PASTAS_GERADAS', 'PASTAS_APROVADAS', 'VISITAS_AGENDADAS'].includes(value)
        ))
      : []
  ));

  return {
    ...normalizeBaseResponse(record, 'sdr-goals'),
    as_of_date: typeof record.as_of_date === 'string' ? record.as_of_date : null,
    available_goal_types: availableGoalTypes,
    goals_configured: record.goals_configured === true,
    is_manual_reference_month: record.is_manual_reference_month === true,
    items: Array.isArray(record.items) ? record.items.map(normalizeSdrGoalItem) : [],
    month_end: String(record.month_end ?? ''),
    month_start: String(record.month_start ?? ''),
    note: typeof record.note === 'string' && record.note.trim() ? record.note : null,
    reference_month: String(record.reference_month ?? ''),
    source_table: String(record.source_table ?? ''),
  };
}

function normalizeSdrConversionItem(value: unknown): CommercialSdrConversionItem {
  const record = asRecord(value);

  return {
    approved_leads: asNumber(record.approved_leads),
    approved_or_conditional_leads: asNumber(record.approved_or_conditional_leads),
    conditional_leads: asNumber(record.conditional_leads),
    folder_to_approved_only_rate_pct: asNullableNumber(record.folder_to_approved_only_rate_pct),
    folder_to_approved_rate_pct: asNullableNumber(record.folder_to_approved_rate_pct),
    folder_to_conditional_rate_pct: asNullableNumber(record.folder_to_conditional_rate_pct),
    folder_to_rejected_rate_pct: asNullableNumber(record.folder_to_rejected_rate_pct),
    generated_folders: asNumber(record.generated_folders),
    mql_leads: asNumber(record.mql_leads),
    mql_to_folder_rate_pct: asNullableNumber(record.mql_to_folder_rate_pct),
    rejected_leads: asNumber(record.rejected_leads),
    sdr: String(record.sdr ?? 'Sem SDR'),
  };
}

function normalizeSdrConversionsResponse(raw: unknown): CommercialSdrConversionsResponse {
  const record = asRecord(raw);
  const totals = asRecord(record.totals);

  return {
    ...normalizeBaseResponse(record, 'sdr-conversions'),
    items: Array.isArray(record.items) ? record.items.map(normalizeSdrConversionItem) : [],
    totals: {
      approved_leads: asNumber(totals.approved_leads),
      conditional_leads: asNumber(totals.conditional_leads),
      generated_folders: asNumber(totals.generated_folders),
      mql_leads: asNumber(totals.mql_leads),
      rejected_leads: asNumber(totals.rejected_leads),
    },
  };
}

const commercialFunnelEventKeys = new Set<CommercialFunnelEventKey>([
  'approval',
  'folder_generated',
  'rejection',
]);

function normalizeFunnelEventKey(value: unknown): CommercialFunnelEventKey {
  const key = String(value ?? '').trim() as CommercialFunnelEventKey;
  return commercialFunnelEventKeys.has(key) ? key : 'folder_generated';
}

function normalizeFunnelEventItem(value: unknown): CommercialFunnelEventItem {
  const record = asRecord(value);
  const eventKey = normalizeFunnelEventKey(record.event_key);

  return {
    channel: String(record.channel ?? 'Não informado'),
    count: asNumber(record.count),
    date: String(record.date ?? ''),
    event_key: eventKey,
    event_label: String(record.event_label ?? eventKey),
    sdr: String(record.sdr ?? 'Sem SDR'),
  };
}

function normalizeFunnelEventsResponse(raw: unknown): CommercialFunnelEventsResponse {
  const record = asRecord(raw);

  return {
    ...normalizeBaseResponse(record, 'funnel-events'),
    days: Array.isArray(record.days) ? record.days.map((day) => String(day)).filter(Boolean) : [],
    event_types: Array.isArray(record.event_types)
      ? record.event_types.map((value) => {
        const item = asRecord(value);
        const key = normalizeFunnelEventKey(item.key);
        return {
          key,
          label: String(item.label ?? key),
        };
      })
      : [],
    items: Array.isArray(record.items) ? record.items.map(normalizeFunnelEventItem) : [],
    selected_sdrs: Array.isArray(record.selected_sdrs)
      ? record.selected_sdrs.map((sdr) => String(sdr)).filter(Boolean)
      : [],
  };
}

function normalizeSdrRankingItem(value: unknown): CommercialSdrRankingItem {
  const record = asRecord(value);
  const lastEventAt = record.last_event_at ?? record.last_approval_at;

  return {
    count: asNumber(record.count ?? record.approvals),
    last_event_at: lastEventAt ? String(lastEventAt) : null,
    previous_count: asNumber(record.previous_count ?? record.previous_approvals),
    rank: asNumber(record.rank),
    sdr: String(record.sdr ?? 'Sem SDR'),
    variation: asNumber(record.variation),
  };
}

function sortAndRankSdrRanking(items: CommercialSdrRankingItem[]): CommercialSdrRankingItem[] {
  const timestamp = (value: string | null): number => {
    if (!value) return Number.POSITIVE_INFINITY;
    const parsedValue = Date.parse(value);
    return Number.isNaN(parsedValue) ? Number.POSITIVE_INFINITY : parsedValue;
  };

  return items
    .filter((item) => item.count > 0)
    .sort((left, right) => (
      right.count - left.count
      || timestamp(left.last_event_at) - timestamp(right.last_event_at)
      || left.sdr.localeCompare(right.sdr, 'pt-BR', { sensitivity: 'base' })
    ))
    .map((item, index) => ({
      ...item,
      rank: index + 1,
    }));
}

function normalizeSdrRankingResponse(raw: unknown): CommercialSdrRankingResponse {
  const record = asRecord(raw);
  const period = asRecord(record.period);
  const today = asRecord(record.today);
  const items = sortAndRankSdrRanking(
    Array.isArray(record.items) ? record.items.map(normalizeSdrRankingItem) : []
  );
  const todayItems = sortAndRankSdrRanking(
    Array.isArray(today.items) ? today.items.map(normalizeSdrRankingItem) : []
  );
  const indicatorValue = String(record.indicator ?? 'approved_folders');
  const indicator: CommercialSdrRankingIndicator = (
    ['approved_folders', 'generated_folders', 'scheduled_visits'].includes(indicatorValue)
      ? indicatorValue
      : 'approved_folders'
  ) as CommercialSdrRankingIndicator;

  return {
    ...normalizeBaseResponse(record, 'sdr-ranking'),
    indicator,
    items,
    outside_top_three_count: items
      .slice(3)
      .reduce((total, item) => total + item.count, 0),
    period: {
      end_date: String(period.end_date ?? ''),
      previous_end_date: String(period.previous_end_date ?? ''),
      previous_start_date: String(period.previous_start_date ?? ''),
      start_date: String(period.start_date ?? ''),
    },
    today: {
      date: String(today.date ?? ''),
      items: todayItems,
      previous_date: String(today.previous_date ?? ''),
      unassigned_count: asNumber(today.unassigned_count ?? today.unassigned_approvals),
    },
    total_count: asNumber(
      record.total_count
      ?? record.total_approvals
      ?? items.reduce((total, item) => total + item.count, 0)
    ),
    unassigned_count: asNumber(record.unassigned_count ?? record.unassigned_approvals),
  };
}

const commercialOpenWorkloadGroupKeys = new Set<CommercialSdrOpenWorkloadGroupKey>([
  'approved_or_conditional',
  'contact_attempt',
  'document_adjustment',
  'document_receipt',
]);

function normalizeOpenWorkloadGroupKey(value: unknown): CommercialSdrOpenWorkloadGroupKey | null {
  const key = String(value ?? '').trim() as CommercialSdrOpenWorkloadGroupKey;
  return commercialOpenWorkloadGroupKeys.has(key) ? key : null;
}

function normalizeSdrOpenWorkloadItem(value: unknown): CommercialSdrOpenWorkloadItem {
  const record = asRecord(value);

  return {
    approved_or_conditional: asNumber(record.approved_or_conditional),
    contact_attempt: asNumber(record.contact_attempt),
    document_adjustment: asNumber(record.document_adjustment),
    document_receipt: asNumber(record.document_receipt),
    sdr: String(record.sdr ?? 'Sem SDR'),
    total_portfolio: asNumber(record.total_portfolio),
  };
}

function normalizeSdrOpenWorkloadResponse(raw: unknown): CommercialSdrOpenWorkloadResponse {
  const record = asRecord(raw);
  const totals = asRecord(record.totals);

  return {
    ...normalizeBaseResponse(record, 'sdr-open-workload'),
    items: Array.isArray(record.items) ? record.items.map(normalizeSdrOpenWorkloadItem) : [],
    stage_groups: Array.isArray(record.stage_groups)
      ? record.stage_groups.flatMap((value) => {
        const item = asRecord(value);
        const key = normalizeOpenWorkloadGroupKey(item.key);
        if (!key) return [];
        return {
          key,
          label: String(item.label ?? key),
        };
      })
      : [],
    totals: {
      approved_or_conditional: asNumber(totals.approved_or_conditional),
      contact_attempt: asNumber(totals.contact_attempt),
      document_adjustment: asNumber(totals.document_adjustment),
      document_receipt: asNumber(totals.document_receipt),
      total_portfolio: asNumber(totals.total_portfolio),
    },
  };
}

function normalizeSdrStageTimeItem(value: unknown): CommercialSdrStageTimeItem {
  const record = asRecord(value);

  return {
    avg_days: asNullableNumber(record.avg_days),
    lead_count: asNumber(record.lead_count),
    max_days: asNullableNumber(record.max_days),
    min_days: asNullableNumber(record.min_days),
    sdr: String(record.sdr ?? 'Sem SDR'),
    stage_index: asNumber(record.stage_index),
    stage_name: String(record.stage_name ?? 'Sem etapa'),
  };
}

function normalizeSdrStageTimeResponse(raw: unknown): CommercialSdrStageTimeResponse {
  const record = asRecord(raw);

  return {
    ...normalizeBaseResponse(record, 'sdr-stage-time'),
    items: Array.isArray(record.items) ? record.items.map(normalizeSdrStageTimeItem) : [],
    max_avg_days: asNumber(record.max_avg_days),
    sdrs: Array.isArray(record.sdrs) ? record.sdrs.map((sdr) => String(sdr)).filter(Boolean) : [],
    stages: Array.isArray(record.stages)
      ? record.stages.map((value) => {
        const item = asRecord(value);
        return {
          stage_index: asNumber(item.stage_index),
          stage_name: String(item.stage_name ?? 'Sem etapa'),
        };
      })
      : [],
  };
}

function normalizeOperationStageTimeItem(value: unknown): CommercialOperationStageTimeItem {
  const record = asRecord(value);
  return {
    avg_days: asNullableNumber(record.avg_days),
    lead_count: asNumber(record.lead_count),
    max_days: asNullableNumber(record.max_days),
    min_days: asNullableNumber(record.min_days),
    stage_index: asNumber(record.stage_index),
    stage_name: String(record.stage_name ?? 'Sem etapa'),
  };
}

function normalizeOperationStuckLeadItem(value: unknown): CommercialOperationStuckLeadItem {
  const record = asRecord(value);
  const status = String(record.status ?? 'attention') as CommercialOperationStuckLeadItem['status'];
  return {
    days_without_contact: asNumber(record.days_without_contact),
    lead_name: String(record.lead_name ?? record.opportunity_id ?? 'Lead'),
    opportunity_id: String(record.opportunity_id ?? ''),
    sdr_name: String(record.sdr_name ?? 'Sem SDR'),
    stage_name: String(record.stage_name ?? 'Sem etapa'),
    status: ['alert', 'attention', 'critical', 'stagnant', 'urgent'].includes(status) ? status : 'attention',
  };
}

function normalizeTrendGranularity(value: unknown): CommercialTrendGranularity {
  const granularity = String(value ?? 'monthly');
  return ['daily', 'monthly', 'weekly'].includes(granularity)
    ? granularity as CommercialTrendGranularity
    : 'monthly';
}

function legacyMonthStart(value: unknown): string {
  const month = String(value ?? '').trim();
  return /^\d{4}-\d{2}$/.test(month) ? `${month}-01` : '';
}

function normalizeOperationMonthlyApprovalItem(value: unknown): CommercialOperationMonthlyApprovalItem {
  const record = asRecord(value);
  return {
    approved_count: asNumber(record.approved_count),
    created_in_period_count: asNumber(record.created_in_period_count),
    month: String(record.month ?? ''),
    scheduled_visits_count: asNumber(record.scheduled_visits_count),
  };
}

function normalizeOperationApprovalEvolutionItem(value: unknown): CommercialOperationApprovalEvolutionItem {
  const record = asRecord(value);
  const legacyStart = legacyMonthStart(record.month);
  return {
    approved_count: asNumber(record.approved_count),
    created_in_period_count: asNumber(record.created_in_period_count),
    granularity: normalizeTrendGranularity(record.granularity),
    period_end: String(record.period_end ?? record.period_start ?? legacyStart),
    period_start: String(record.period_start ?? legacyStart),
    scheduled_visits_count: asNumber(record.scheduled_visits_count),
  };
}

function normalizeOperationConversionItem(value: unknown): CommercialOperationConversionItem {
  const record = asRecord(value);
  const month = String(record.month ?? '');
  return {
    approved_leads: asNumber(record.approved_leads),
    cpf_clean_to_folder_rate_pct: asNumber(record.cpf_clean_to_folder_rate_pct),
    folder_to_approved_rate_pct: asNumber(record.folder_to_approved_rate_pct),
    granularity: normalizeTrendGranularity(record.granularity),
    generated_folders: asNumber(record.generated_folders),
    global_conversion_rate_pct: asNumber(record.global_conversion_rate_pct),
    month,
    mql_leads: asNumber(record.mql_leads),
    period_end: String(record.period_end ?? record.period_start ?? legacyMonthStart(month)),
    period_start: String(record.period_start ?? legacyMonthStart(month)),
    total_leads: asNumber(record.total_leads),
  };
}

function normalizeOperationCycleItem(value: unknown): CommercialOperationCycleItem {
  const record = asRecord(value);
  const month = String(record.month ?? '');
  return {
    avg_cycle_days: asNumber(record.avg_cycle_days),
    granularity: normalizeTrendGranularity(record.granularity),
    lost_avg_cycle_days: asNumber(record.lost_avg_cycle_days),
    lost_count: asNumber(record.lost_count),
    month,
    outcome_count: asNumber(record.outcome_count),
    period_end: String(record.period_end ?? record.period_start ?? legacyMonthStart(month)),
    period_start: String(record.period_start ?? legacyMonthStart(month)),
    won_avg_cycle_days: asNumber(record.won_avg_cycle_days),
    won_count: asNumber(record.won_count),
  };
}

function normalizeOperationTrendSeries<T>(
  value: unknown,
  normalizeItem: (item: unknown) => T,
  monthlyFallback: T[]
): CommercialOperationTrendSeries<T> {
  const record = asRecord(value);
  const normalizeItems = (items: unknown) => (
    Array.isArray(items) ? items.map(normalizeItem) : []
  );

  const monthly = normalizeItems(record.monthly);
  return {
    daily: normalizeItems(record.daily),
    monthly: monthly.length > 0 ? monthly : monthlyFallback,
    weekly: normalizeItems(record.weekly),
  };
}

function normalizeLossReasonItem(value: unknown): CommercialOperationMetricsResponse['loss_reasons'][number] {
  const record = asRecord(value);
  const reason = String(record.reason ?? '').trim();
  return {
    previous_volume: asNumber(record.previous_volume),
    reason: reason || 'Não informado',
    share_pct: asNullableNumber(record.share_pct),
    variation_pct: asNullableNumber(record.variation_pct),
    volume: asNumber(record.volume),
  };
}

function normalizeOperationMetricsResponse(raw: unknown): CommercialOperationMetricsResponse {
  const record = asRecord(raw);
  const monthlyApprovals = Array.isArray(record.monthly_approvals)
    ? record.monthly_approvals.map(normalizeOperationMonthlyApprovalItem)
    : [];
  const monthlyApprovalEvolution = monthlyApprovals.map((item) => normalizeOperationApprovalEvolutionItem(item));
  const monthlyConversions = Array.isArray(record.conversion_evolution)
    ? record.conversion_evolution.map(normalizeOperationConversionItem)
    : [];
  const monthlyCycles = Array.isArray(record.cycle_evolution)
    ? record.cycle_evolution.map(normalizeOperationCycleItem)
    : [];

  return {
    ...normalizeBaseResponse(record, 'operation-metrics'),
    approval_evolution: normalizeOperationTrendSeries(
      record.approval_evolution,
      normalizeOperationApprovalEvolutionItem,
      monthlyApprovalEvolution
    ),
    conversion_evolution: monthlyConversions,
    conversion_evolution_series: normalizeOperationTrendSeries(
      record.conversion_evolution_series,
      normalizeOperationConversionItem,
      monthlyConversions
    ),
    cycle_evolution: monthlyCycles,
    cycle_evolution_series: normalizeOperationTrendSeries(
      record.cycle_evolution_series,
      normalizeOperationCycleItem,
      monthlyCycles
    ),
    loss_reasons: Array.isArray(record.loss_reasons)
      ? record.loss_reasons.map(normalizeLossReasonItem)
      : [],
    monthly_approvals: monthlyApprovals,
    stage_times: Array.isArray(record.stage_times)
      ? record.stage_times.map(normalizeOperationStageTimeItem)
      : [],
    stuck_leads: Array.isArray(record.stuck_leads)
      ? record.stuck_leads.map(normalizeOperationStuckLeadItem)
      : [],
    total_losses: asNumber(record.total_losses),
  };
}

function normalizeInterestRegionItem(value: unknown): CommercialInterestRegionItem {
  const record = asRecord(value);
  return {
    approved_folders: asNumber(record.approved_folders),
    approved_folders_pct: asNumber(record.approved_folders_pct),
    new_leads: asNumber(record.new_leads),
    new_leads_pct: asNumber(record.new_leads_pct),
    region: String(record.region ?? 'Outra localidade'),
  };
}

function normalizeInterestRegionsResponse(raw: unknown): CommercialInterestRegionsResponse {
  const record = asRecord(raw);
  return {
    ...normalizeBaseResponse(record, 'interest-regions'),
    approval_region_coverage_pct: asNumber(record.approval_region_coverage_pct),
    approved_folders: asNumber(record.approved_folders),
    identified_leads: asNumber(record.identified_leads),
    items: Array.isArray(record.items)
      ? record.items.map(normalizeInterestRegionItem)
      : [],
    lead_region_coverage_pct: asNumber(record.lead_region_coverage_pct),
    total_approved_folders: asNumber(record.total_approved_folders),
    total_leads: asNumber(record.total_leads),
  };
}

function normalizeSankeyLinkItem(value: unknown): CommercialSankeyLinkItem {
  const record = asRecord(value);
  return {
    source: String(record.source ?? ''),
    target: String(record.target ?? ''),
    value: asNumber(record.value),
  };
}

function normalizeCpfQualityItem(value: unknown): CommercialCpfQualityItem {
  const record = asRecord(value);
  return {
    channel: String(record.channel ?? 'Não informado'),
    known_clean: asNumber(record.known_clean),
    known_clean_pct: asNullableNumber(record.known_clean_pct),
    known_dirty: asNumber(record.known_dirty),
    known_dirty_pct: asNullableNumber(record.known_dirty_pct),
    total_leads: asNumber(record.total_leads),
    unknown: asNumber(record.unknown),
    unknown_pct: asNullableNumber(record.unknown_pct),
  };
}

function normalizeChannelsQualityResponse(raw: unknown): CommercialChannelsQualityResponse {
  const record = asRecord(raw);
  const sankey = asRecord(record.sankey);
  return {
    ...normalizeBaseResponse(record, 'channels-quality'),
    channels: Array.isArray(record.channels) ? record.channels.map(normalizeChannelQualityItem) : [],
    cpf_quality: Array.isArray(record.cpf_quality) ? record.cpf_quality.map(normalizeCpfQualityItem) : [],
    sankey: {
      links: Array.isArray(sankey.links) ? sankey.links.map(normalizeSankeyLinkItem) : [],
    },
  };
}

function normalizeSdrOutcomeTimeItem(value: unknown): CommercialSdrOutcomeTimeItem {
  const record = asRecord(value);
  return {
    approval_count: asNumber(record.approval_count),
    approval_median_days: asNullableNumber(record.approval_median_days),
    disqualification_count: asNumber(record.disqualification_count),
    disqualification_median_days: asNullableNumber(record.disqualification_median_days),
    sdr_name: String(record.sdr_name ?? 'Sem SDR'),
  };
}

function normalizeSdrOutcomeTimeResponse(raw: unknown): CommercialSdrOutcomeTimeResponse {
  const record = asRecord(raw);
  return {
    ...normalizeBaseResponse(record, 'sdr-outcome-time'),
    items: Array.isArray(record.items) ? record.items.map(normalizeSdrOutcomeTimeItem) : [],
  };
}

function normalizeTeamGoalProgressResponse(raw: unknown): CommercialTeamGoalProgressResponse {
  const record = asRecord(raw);
  const actuals = asRecord(record.actuals);

  return {
    ...normalizeBaseResponse(record, 'team-goal-progress'),
    actuals: {
      PASTAS_APROVADAS: asNumber(actuals.PASTAS_APROVADAS),
      PASTAS_GERADAS: asNumber(actuals.PASTAS_GERADAS),
      VISITAS_AGENDADAS: asNumber(actuals.VISITAS_AGENDADAS),
    },
    reference_month: String(record.reference_month ?? ''),
  };
}

export class CommercialRepository {
  private httpRepository = new HttpRepository();

  private async getResource(route: CommercialRoute, params: CommercialFetchParams, accessToken: string): Promise<unknown> {
    const url = appendQuery(getHabitaCommercialResourceUrl(route), params);
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'X-Habita-Authorization': `Bearer ${accessToken}`,
    };
    const response = await this.httpRepository.get<unknown>(url, { headers });
    return response.data;
  }

  async fetchOverview(params: CommercialFetchParams, accessToken: string): Promise<CommercialOverviewResponse> {
    return normalizeOverviewResponse(await this.getResource('overview', params, accessToken));
  }

  async fetchFilterOptions(
    params: CommercialFetchParams,
    accessToken: string
  ): Promise<CommercialFilterOptionsResponse> {
    return normalizeOptionsResponse(await this.getResource('filter-options', params, accessToken));
  }

  async fetchFunnel(params: CommercialFetchParams, accessToken: string): Promise<CommercialFunnelResponse> {
    return normalizeFunnelResponse(await this.getResource('funnel', params, accessToken));
  }

  async fetchFunnelEvents(
    params: CommercialFetchParams,
    accessToken: string
  ): Promise<CommercialFunnelEventsResponse> {
    return normalizeFunnelEventsResponse(await this.getResource('funnel-events', params, accessToken));
  }

  async fetchSdrRanking(
    params: CommercialFetchParams,
    accessToken: string
  ): Promise<CommercialSdrRankingResponse> {
    return normalizeSdrRankingResponse(await this.getResource('sdr-ranking', params, accessToken));
  }

  async fetchSdrGoals(params: CommercialFetchParams, accessToken: string): Promise<CommercialSdrGoalsResponse> {
    return normalizeSdrGoalsResponse(await this.getResource('sdr-goals', params, accessToken));
  }

  async fetchSdrConversions(
    params: CommercialFetchParams,
    accessToken: string
  ): Promise<CommercialSdrConversionsResponse> {
    return normalizeSdrConversionsResponse(await this.getResource('sdr-conversions', params, accessToken));
  }

  async fetchSdrOpenWorkload(
    params: CommercialFetchParams,
    accessToken: string
  ): Promise<CommercialSdrOpenWorkloadResponse> {
    return normalizeSdrOpenWorkloadResponse(await this.getResource('sdr-open-workload', params, accessToken));
  }

  async fetchSdrStageTime(
    params: CommercialFetchParams,
    accessToken: string
  ): Promise<CommercialSdrStageTimeResponse> {
    return normalizeSdrStageTimeResponse(await this.getResource('sdr-stage-time', params, accessToken));
  }

  async fetchTeamGoalProgress(
    params: CommercialFetchParams,
    accessToken: string
  ): Promise<CommercialTeamGoalProgressResponse> {
    return normalizeTeamGoalProgressResponse(
      await this.getResource('team-goal-progress', params, accessToken)
    );
  }

  async fetchOperationMetrics(
    params: CommercialFetchParams,
    accessToken: string
  ): Promise<CommercialOperationMetricsResponse> {
    return normalizeOperationMetricsResponse(await this.getResource('operation-metrics', params, accessToken));
  }

  async fetchInterestRegions(
    params: CommercialFetchParams,
    accessToken: string
  ): Promise<CommercialInterestRegionsResponse> {
    return normalizeInterestRegionsResponse(await this.getResource('interest-regions', params, accessToken));
  }

  async fetchChannelsQuality(
    params: CommercialFetchParams,
    accessToken: string
  ): Promise<CommercialChannelsQualityResponse> {
    return normalizeChannelsQualityResponse(await this.getResource('channels-quality', params, accessToken));
  }

  async fetchSdrOutcomeTime(
    params: CommercialFetchParams,
    accessToken: string
  ): Promise<CommercialSdrOutcomeTimeResponse> {
    return normalizeSdrOutcomeTimeResponse(await this.getResource('sdr-outcome-time', params, accessToken));
  }
}
